import { Link, useNavigate } from "react-router";
import { LogOut, Settings, Shield, User } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initialsFromName } from "@/lib/utils";

// Single user menu used by both admin and portal shells. Replaces the legacy
// /components/admin/UserMenu.tsx — same dropdown shape but the sign-out goes
// through useAuth().signOut() (which clears the worker session cookie via
// /api/auth/signout, then supabase.auth.signOut(), then resets query cache)
// instead of posting a form to the API.
export function UserMenu({
  email,
  fullName,
  role,
}: {
  email: string;
  fullName: string | null;
  role: "internal" | "partner";
}) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const display = fullName ?? email;

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Account menu"
          className="inline-flex items-center gap-2.5 rounded-full border bg-card px-2 py-1 pl-1 text-sm transition hover:bg-muted"
        >
          <Avatar className="h-7 w-7">
            <AvatarFallback className="bg-primary text-[11px] text-primary-foreground">
              {initialsFromName(display)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[160px] truncate font-medium text-foreground sm:inline">
            {display}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="font-medium">{display}</div>
          <div className="text-xs font-normal text-muted-foreground">{email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Shield className="h-3 w-3" />
            {role === "internal" ? "Bookboost staff" : "Partner"}
          </span>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={role === "internal" ? "/admin/settings" : "/portal/profile"}>
            <User className="mr-2 h-4 w-4" />
            <span>{role === "internal" ? "Settings" : "Profile"}</span>
          </Link>
        </DropdownMenuItem>
        {role === "partner" && (
          <DropdownMenuItem asChild>
            <Link to="/portal/settings">
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
